import React from 'react'
import { useLogic } from '~common/ui-logic/hooks/logic'
import styled from 'styled-components'
import { Checkbox } from '~common/components/checkbox'
import { Button } from '~common/components/button'
import { DatePicker } from '~/features/ui-components/date-picker'
import { LoadingIndicator } from '~common/components/loading'
import { Icon } from '~common/components/icon'
import {
    ExportColumnSelectionLogic,
    type ExportColumnSelectionDependencies,
} from './logic'
import { useAppContext } from '~/app-context'
import { DateRange } from '../email-importer/services/utils'

export function ExportColumnSelection(
    props: ExportColumnSelectionDependencies,
) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(ExportColumnSelectionLogic, {
        ...props,
        ...appContext,
    })

    const allColumns = logic.getAllColumns()
    const columnLabels: Record<string, string> =
        props.exportType === 'bank-transactions'
            ? {
                  id: 'ID',
                  accountIban: 'Account IBAN',
                  vendorId: 'Vendor ID',
                  vendorName: 'Vendor Name',
                  counterpartyIban: 'Counterparty IBAN',
                  date: 'Date',
                  amount: 'Amount',
                  currency: 'Currency',
                  referenceText: 'Reference Text',
                  note: 'Note',
                  categoryId: 'Category ID',
                  categoryLabel: 'Category Label',
                  matchingInvoiceUrl: 'Matching Invoice URL',
                  status: 'Status',
                  createdAt: 'Created At',
                  updatedAt: 'Updated At',
              }
            : {
                  id: 'ID',
                  date: 'Date',
                  amount: 'Amount',
                  netAmount: 'Net Amount',
                  currency: 'Currency',
                  vatRate: 'VAT Rate',
                  vatAmount: 'VAT Amount',
                  invoiceId: 'Invoice ID',
                  creditCard: 'Credit Card',
                  vendorName: 'Vendor Name',
                  vendorAddress: 'Vendor Address',
                  vendorCountry: 'Vendor Country',
                  vendorIbans: 'Vendor IBANs',
                  vendorEmailAddresses: 'Vendor Email Addresses',
                  reason: 'Reason',
                  googleDriveFileId: 'Google Drive File ID',
                  googleDriveFileLink: 'Google Drive File Link',
                  sourceType: 'Source Type',
                  createdAt: 'Created At',
                  updatedAt: 'Updated At',
              }

    return (
        <Container>
            {state.exportState === 'idle' && (
                <>
                    <Title>
                        Select Columns to Export{' '}
                        {props.exportType === 'bank-transactions'
                            ? '(Bank Transactions)'
                            : '(Invoices)'}
                    </Title>

                    <DateRangeSection>
                        <DateRangeLabel>Date Range</DateRangeLabel>
                        <DatePicker
                            onConfirm={(range: DateRange | Date | undefined) =>
                                logic.setDateRange(range as DateRange)
                            }
                            placeholder="Select date range"
                            size="medium"
                            width="100%"
                        />
                    </DateRangeSection>

                    <ColumnsSection>
                        <ColumnsLabel>Columns</ColumnsLabel>
                        <ColumnsGrid>
                            {allColumns.map((column) => (
                                <Checkbox
                                    key={column}
                                    id={column}
                                    checked={state.selectedColumns.has(column)}
                                    onChange={(checked) => {
                                        logic.toggleColumn(column, checked)
                                    }}
                                    label={columnLabels[column] || column}
                                />
                            ))}
                        </ColumnsGrid>
                    </ColumnsSection>

                    <Actions>
                        <Button
                            onClick={() => props.onClose()}
                            label="Cancel"
                            variant="outline"
                        />
                        <Button
                            onClick={() => logic.exportToGoogleSheets()}
                            label="Export to Google Sheets"
                            variant="primary"
                        />
                    </Actions>
                </>
            )}

            {state.exportState === 'exporting' && (
                <ExportingState>
                    <LoadingIndicator size="large" />
                    <StateTitle>Exporting to Google Sheets</StateTitle>
                    <StateDescription>
                        Please wait while we export your data...
                    </StateDescription>
                </ExportingState>
            )}

            {state.exportState === 'success' && (
                <SuccessState>
                    <SuccessIcon>
                        <Icon name="check" size="48" />
                    </SuccessIcon>
                    <StateTitle>Export Complete!</StateTitle>
                    <StateDescription>
                        Your data has been successfully exported to Google
                        Sheets.
                    </StateDescription>
                    <Actions>
                        <Button
                            onClick={() => logic.openExportedSpreadsheet()}
                            label="Open Spreadsheet"
                            variant="primary"
                        />
                        <Button
                            onClick={() => props.onClose()}
                            label="Close"
                            variant="outline"
                        />
                    </Actions>
                </SuccessState>
            )}

            {state.exportState === 'error' && (
                <ErrorState>
                    <ErrorIcon>
                        <Icon name="close" size="48" />
                    </ErrorIcon>
                    <StateTitle>Export Failed</StateTitle>
                    <StateDescription>
                        {state.exportError ||
                            'An error occurred during export.'}
                    </StateDescription>
                    <Actions>
                        <Button
                            onClick={() => {
                                logic.setState({ exportState: 'idle' })
                            }}
                            label="Try Again"
                            variant="primary"
                        />
                        <Button
                            onClick={() => props.onClose()}
                            label="Close"
                            variant="outline"
                        />
                    </Actions>
                </ErrorState>
            )}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[6]};
    padding: ${({ theme }) => theme.spacing[4]};
    min-width: 500px;
    max-width: 700px;
`

const Title = styled.div`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const DateRangeSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

const DateRangeLabel = styled.label`
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
`

const ColumnsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
`

const ColumnsLabel = styled.label`
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
`

const ColumnsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing[3]};
    max-height: 400px;
    overflow-y: auto;
    padding: ${({ theme }) => theme.spacing[2]};
`

const Actions = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[3]};
    justify-content: flex-end;
    margin-top: ${({ theme }) => theme.spacing[4]};
`

const ExportingState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.spacing[4]};
    min-height: 200px;
    justify-content: center;
`

const SuccessState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.spacing[4]};
    min-height: 200px;
    justify-content: center;
`

const ErrorState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.spacing[4]};
    min-height: 200px;
    justify-content: center;
`

const StateTitle = styled.h3`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const StateDescription = styled.p`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`

const SuccessIcon = styled.div`
    color: ${({ theme }) => theme.colors.success};
`

const ErrorIcon = styled.div`
    color: ${({ theme }) => theme.colors.error};
`
